// client/src/lib/apiClient.ts
// Typed API client for AGOC School.
//
// Responsibilities:
//   - Prepend VITE_API_BASE_URL to every request path
//   - Automatically attach 'Authorization: Bearer <token>' from Firebase Auth
//   - Throw ApiError for non-2xx responses with a structured error body
//   - Token is fetched lazily on every call (Firebase caches it; only refreshes when expired)
//
// Usage:
//   import { apiClient } from '@/lib/apiClient';
//   const data = await apiClient.get<TenantConfig>('/tenant-config');
//   await apiClient.post('/ai/query', { query: 'How do I...', topK: 5 });
//
// The base URL routes through the API Gateway in production and through the local
// Local BFF default port is 4001 (see server/.env.example — macOS binds 127.0.0.1:4000).

import { getIdToken } from '@/lib/firebase';

// ── Types ───────────────────────────────────────────────────────────────────

/** Structured error thrown by ApiClient when the server returns a non-2xx status. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Expected error JSON body shape from AGOC platform services. */
interface ErrorBody {
  error?: {
    code?: string;
    message?: string;
  };
  message?: string;
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  /** Skip attaching the Authorization header (only for /health or public endpoints). */
  skipAuth?: boolean;
  /** Additional headers merged into the request. */
  headers?: Record<string, string>;
}

// ── Base URL ────────────────────────────────────────────────────────────────
const BASE_URL: string = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? '';

// ── Core fetch wrapper ───────────────────────────────────────────────────────

async function request<T>(
  method: HttpMethod,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (!options.skipAuth) {
    // Fetch the current Firebase ID token (includes tenantId + roles custom claims)
    const token = await getIdToken();
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${BASE_URL}${path}`;

  let response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // If the BFF rejected the token with 401 and the request was authenticated,
  // force-refresh the token once (claims may have been set after the cached token
  // was issued) and retry. If the retry also fails, fall through to the error handler.
  if (response.status === 401 && !options.skipAuth) {
    const freshToken = await getIdToken(true);
    headers['Authorization'] = `Bearer ${freshToken}`;
    response = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }

  if (!response.ok) {
    // Parse structured error body from platform services
    let errorBody: ErrorBody = {};
    try {
      errorBody = (await response.json()) as ErrorBody;
    } catch {
      // Ignore parse errors; fall through to generic message
    }

    const code = errorBody.error?.code ?? 'API_ERROR';
    const message =
      errorBody.error?.message ?? errorBody.message ?? response.statusText;

    throw new ApiError(response.status, code, message);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  return response.json() as Promise<T>;
}

// ── Public API ───────────────────────────────────────────────────────────────

export const apiClient = {
  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('GET', path, undefined, options);
  },

  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('POST', path, body, options);
  },

  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PUT', path, body, options);
  },

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PATCH', path, body, options);
  },

  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('DELETE', path, undefined, options);
  },
};
